import React, { useState, useRef } from 'react';
import { NETWORKS } from '../config/networks';

const SUPPORTED_NETWORKS = [
  { id: 'base',        name: 'Base',         chainId: 8453,  icon: '🔵' },
  { id: 'baseSepolia', name: 'Base Sepolia', chainId: 84532, icon: '🧪', testnet: true },
];

const STABLECOINS = [
  { symbol: 'USDC', name: 'USD Coin', icon: '💵' },
  // { symbol: 'USDT', name: 'Tether', icon: '💲' },
  // { symbol: 'DAI',  name: 'DAI',    icon: '◈'  }
];

const BILLING_INTERVALS = [
  { value: 'monthly',   label: 'Monthly',   days: 30,  sublabel: '30d',  periodsPerYear: 12 },
  { value: 'quarterly', label: 'Quarterly', days: 90,  sublabel: '90d',  periodsPerYear: 4  },
  { value: 'yearly',    label: 'Yearly',    days: 365, sublabel: '365d', periodsPerYear: 1  }
];

const fmtAmt = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const inputClass = "w-full bg-white/[0.05] border border-white/[0.1] rounded-xl text-white/80 placeholder:text-white/30 focus:border-cyan-500/50 focus:ring-0 outline-none px-4 py-2.5 text-sm transition-all";
const labelClass = "block text-[10px] font-medium text-white/35 uppercase tracking-widest mb-1.5";

function PayLinkGenerator({ onClose, merchantAddress, subsAddress, usdcAddress, chainId }) {
  const defaultNetwork = SUPPORTED_NETWORKS.find(n => n.chainId === chainId)?.id || 'base';
  const [formData, setFormData] = useState({
    planName: 'Premium',
    features: 'Unlimited access to all issues\nWeekly newsletter delivered to your inbox\nExclusive subscriber-only content\nFull archive access\nEarly access to new features\nCancel anytime',
    price: '29.99',
    network: defaultNetwork,
    token: 'USDC',
    billingInterval: 'monthly',
    reminderDays: '3',
    annualDiscount: '20',
    walletAddress: merchantAddress || ''
  });

  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [previewAnnual, setPreviewAnnual] = useState(false);
  const linkRef = useRef(null);

  const handleGenerate = () => {
    const net = NETWORKS[formData.network];
    const resolvedSubsAddress = net?.subsAddress || subsAddress;
    const resolvedUsdcAddress = net?.usdcAddress || usdcAddress;
    const resolvedChainId     = net?.chainId     || chainId;
    const intervalDays = BILLING_INTERVALS.find(i => i.value === formData.billingInterval)?.days ?? 30;
    const featuresPiped = formData.features.split('\n').map(f => f.trim()).filter(Boolean).join('|');
    const params = new URLSearchParams({
      contract: resolvedSubsAddress,
      usdc: resolvedUsdcAddress,
      merchant: formData.walletAddress,
      chain: resolvedChainId,
      plan: formData.planName,
      features: featuresPiped,
      price: formData.price,
      interval: intervalDays * 86400,
      discount: formData.annualDiscount,
      token: formData.token,
      reminder: formData.reminderDays,
    });
    const link = `${window.location.origin}${window.location.pathname}#payment?${params.toString()}`;
    setGeneratedLink(link);
    setTimeout(() => linkRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const selectedInterval = BILLING_INTERVALS.find(i => i.value === formData.billingInterval) ?? BILLING_INTERVALS[0];
  const yearlyPrice = (parseFloat(formData.price) * selectedInterval.periodsPerYear * (1 - parseFloat(formData.annualDiscount) / 100)).toFixed(2);
  // Already billing annually — there's nothing to discount up to.
  const canOfferAnnual = formData.billingInterval !== 'yearly' && parseFloat(formData.annualDiscount) > 0;

  const toggleBtn = (active) =>
    `px-3 py-2.5 rounded-xl border transition-all text-center text-sm font-medium ${
      active
        ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-400'
        : 'border-white/[0.1] hover:border-white/[0.2] text-white/50 hover:text-white/70'
    }`;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { e.currentTarget._mbdown = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.currentTarget._mbdown) onClose(); }}
    >
      <div
        className="bg-[#0d1117] border border-white/[0.08] rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-7 pt-7 pb-5 border-b border-white/[0.06] sticky top-0 bg-[#0d1117] z-10 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Create Payment Link</h2>
              <p className="text-sm text-white/50 mt-1">Generate a shareable subscription link</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center hover:bg-white/[0.08] rounded-full transition-colors text-white/40 hover:text-white"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-x divide-white/[0.06]">

          {/* Left — Configuration */}
          <div className="px-6 py-5 space-y-5">
            <p className="text-[10px] uppercase tracking-widest text-white/35 font-medium">Configuration</p>

            {/* Plan Name */}
            <div>
              <label className={labelClass}>Plan Name</label>
              <input
                type="text"
                value={formData.planName}
                onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
                placeholder="e.g., Premium Plan"
                className={inputClass}
              />
            </div>

            {/* Plan Features */}
            <div>
              <label className={labelClass}>Plan Features</label>
              <textarea
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                placeholder="One feature per line"
                rows={7}
                className={inputClass + " resize-none"}
              />
              <p className="text-xs text-white/30 mt-1.5">One feature per line. Shown on the payment page.</p>
            </div>

            {/* Price */}
            <div>
              <label className={labelClass}>Price</label>
              <div className="relative">
                <span className="absolute left-4 top-2.5 text-white/30 font-medium text-sm">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="29.99"
                  className={inputClass + " pl-8"}
                />
              </div>
            </div>

            {/* Billing Interval */}
            <div>
              <label className={labelClass}>Billing Interval</label>
              <div className="grid grid-cols-3 gap-2">
                {BILLING_INTERVALS.map(interval => (
                  <button
                    key={interval.value}
                    onClick={() => { setFormData({ ...formData, billingInterval: interval.value }); setPreviewAnnual(false); }}
                    className={toggleBtn(formData.billingInterval === interval.value)}
                  >
                    <div>{interval.label}</div>
                    <div className="text-xs mt-0.5 opacity-60">{interval.sublabel}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Annual Discount */}
            <div>
              <label className={labelClass}>Annual Subscription Discount (%)</label>
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                value={formData.annualDiscount}
                onChange={(e) => setFormData({ ...formData, annualDiscount: e.target.value })}
                placeholder="20"
                className={inputClass}
              />
              {canOfferAnnual ? (
                <p className="text-xs text-white/40 mt-1.5">
                  Yearly price: <span className="font-semibold text-emerald-400">${fmtAmt(yearlyPrice)}/year</span> (save {formData.annualDiscount}%)
                </p>
              ) : (
                <p className="text-xs text-white/30 mt-1.5">
                  {formData.billingInterval === 'yearly'
                    ? 'Already billing annually — no further discount to offer.'
                    : 'Set a discount % to offer customers an annual upgrade.'}
                </p>
              )}
            </div>

            {/* Stablecoin */}
            <div>
              <label className={labelClass}>Payment Token</label>
              <div className="grid grid-cols-3 gap-2">
                {STABLECOINS.map(token => (
                  <button
                    key={token.symbol}
                    onClick={() => setFormData({ ...formData, token: token.symbol })}
                    className={toggleBtn(formData.token === token.symbol)}
                  >
                    <div className="text-lg mb-0.5">{token.icon}</div>
                    <div>{token.symbol}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Network */}
            <div>
              <label className={labelClass}>Blockchain Network</label>
              <div className="grid grid-cols-2 gap-2">
                {SUPPORTED_NETWORKS.map(network => (
                  <button
                    key={network.id}
                    onClick={() => setFormData({ ...formData, network: network.id })}
                    className={toggleBtn(formData.network === network.id) + " text-left"}
                  >
                    <span className="mr-1.5">{network.icon}</span>
                    <span>{network.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Renewal Reminder */}
            <div>
              <label className={labelClass}>Renewal Reminder (days before)</label>
              <input
                type="number"
                step="1"
                min="0"
                value={formData.reminderDays}
                onChange={(e) => setFormData({ ...formData, reminderDays: e.target.value })}
                placeholder="3"
                className={inputClass}
              />
              <p className="text-xs text-white/30 mt-1.5">Set to 0 to disable.</p>
            </div>

            {/* Merchant Wallet Address */}
            <div>
              <label className={labelClass}>Merchant Wallet Address</label>
              <div className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-xl font-mono text-sm text-white/50 select-all break-all">
                {formData.walletAddress || '—'}
              </div>
              <p className="text-xs text-white/30 mt-1.5">Payments will be sent to your connected wallet.</p>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-semibold py-3.5 rounded-xl transition-all text-sm"
            >
              Generate Payment Link
            </button>

            {/* Generated Link */}
            {generatedLink && (
              <div ref={linkRef} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-emerald-400 flex items-center gap-1.5">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Link Generated
                  </span>
                  <button
                    onClick={handleCopy}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      copied
                        ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-400'
                        : 'bg-white/[0.06] border border-white/[0.1] text-white/60 hover:text-white'
                    }`}
                  >
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
                <div className="bg-black/20 border border-white/[0.06] rounded-lg px-3 py-2 font-mono text-xs text-white/50 break-all">
                  {generatedLink}
                </div>
                <p className="text-xs text-white/40 mt-2">Share this link with your customers to accept subscriptions.</p>
              </div>
            )}
          </div>

          {/* Right — Preview */}
          <div className="px-6 py-5 space-y-4">
            <p className="text-[10px] uppercase tracking-widest text-white/35 font-medium">Preview</p>
            <p className="text-xs text-white/30">What your customers will see on the payment page.</p>
            <div className="bg-slate-100 rounded-xl p-4">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="px-5 pt-5 pb-3">
                  <h3 className="text-base font-semibold text-slate-900">{formData.planName}</h3>

                  {canOfferAnnual && (
                    <div className="flex items-center gap-1 mt-2.5 bg-slate-100 rounded-lg p-0.5 w-fit">
                      <button
                        onClick={() => setPreviewAnnual(false)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                          !previewAnnual ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                        }`}
                      >
                        {selectedInterval.label}
                      </button>
                      <button
                        onClick={() => setPreviewAnnual(true)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                          previewAnnual ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                        }`}
                      >
                        Annual
                        <span className="ml-1 text-emerald-600">-{formData.annualDiscount}%</span>
                      </button>
                    </div>
                  )}

                  <div className="flex items-baseline gap-1.5 mt-2">
                    <span className="text-3xl font-bold text-slate-900">
                      ${previewAnnual && canOfferAnnual ? fmtAmt(yearlyPrice) : fmtAmt(formData.price)}
                    </span>
                    <span className="text-sm text-slate-500">
                      {formData.token} / {previewAnnual && canOfferAnnual
                        ? 'year'
                        : formData.billingInterval === 'monthly' ? 'month' : formData.billingInterval === 'quarterly' ? 'quarter' : 'year'}
                    </span>
                  </div>
                  {previewAnnual && canOfferAnnual && (
                    <p className="text-xs text-emerald-600 font-medium mt-1">
                      ${fmtAmt(parseFloat(yearlyPrice) / 12)}/mo billed annually
                    </p>
                  )}
                </div>

                <div className="px-5 pb-4">
                  {formData.features.split('\n').filter(Boolean).map((f, i) => (
                    <div key={i} className="flex items-center gap-2.5 py-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500 flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
                      <span className="text-sm text-slate-700">{f.trim()}</span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-slate-100 mx-5" />

                <div className="px-5 py-4">
                  <p className="text-sm text-slate-500 mb-3">Connect your wallet to subscribe</p>
                  <div className="w-full bg-slate-800 text-white font-medium py-3 px-5 rounded-lg text-sm text-center">
                    Connect MetaMask
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default PayLinkGenerator;
